/**
 * Printers Store Module
 * Manages printer configuration, status, and print jobs
 */

import PrinterService from "@/services/printerService";

const state = {
  printers: [],
  printJobs: [],
  activeConnections: new Map(),
  printQueue: [],
  loading: false,
  error: null,
  lastUpdated: null,
  statistics: {
    onlinePrinters: 0,
    offlinePrinters: 0,
    todayPrintJobs: 0,
    pendingPrintJobs: 0,
    failedPrintJobs: 0,
  },
};

const getters = {
  // Get all printers
  printers: (state) => state.printers,

  // Get printers by connection status
  onlinePrinters: (state) =>
    state.printers.filter((printer) => printer.connection_status === "online"),
  offlinePrinters: (state) =>
    state.printers.filter((printer) => printer.connection_status === "offline"),

  // Get default kitchen printer
  defaultKitchenPrinter: (state) =>
    state.printers.find(
      (printer) =>
        printer.is_default_kitchen &&
        printer.connection_status === "online" &&
        printer.print_categories.includes("kitchen")
    ) || null,

  // Get available printers for specific category
  availablePrinters: (state) => (category) =>
    state.printers.filter(
      (printer) =>
        printer.connection_status === "online" &&
        printer.print_categories.includes(category)
    ),

  // Get printer by ID
  printerById: (state) => (id) =>
    state.printers.find((printer) => printer.id === id),

  // Get all print jobs
  printJobs: (state) => state.printJobs,

  // Get print jobs by status
  printJobsByStatus: (state) => (status) =>
    state.printJobs.filter((job) => job.status === status),

  // Get pending print jobs
  pendingPrintJobs: (state) =>
    state.printJobs.filter((job) => job.status === "pending"),

  // Get failed print jobs
  failedPrintJobs: (state) =>
    state.printJobs.filter((job) => job.status === "failed"),

  // Get print queue
  printQueue: (state) => state.printQueue,

  // Get loading state
  loading: (state) => state.loading,

  // Get error
  error: (state) => state.error,

  // Get statistics
  statistics: (state) => state.statistics,

  // Get last updated timestamp
  lastUpdated: (state) => state.lastUpdated,

  // Check if any printer is online for category
  hasAvailablePrinter: (state) => (category) => {
    return state.printers.some(
      (printer) =>
        printer.connection_status === "online" &&
        printer.print_categories.includes(category)
    );
  },

  // Get printers count by type
  printersByType: (state) => {
    const counts = {};
    state.printers.forEach((printer) => {
      counts[printer.type] = (counts[printer.type] || 0) + 1;
    });
    return counts;
  },

  // Get printers by category
  printersByCategory: (state) => {
    const categoryPrinters = {};
    state.printers.forEach((printer) => {
      printer.print_categories.forEach((category) => {
        if (!categoryPrinters[category]) {
          categoryPrinters[category] = [];
        }
        categoryPrinters[category].push(printer);
      });
    });
    return categoryPrinters;
  },
};

const mutations = {
  // Set loading state
  SET_LOADING(state, loading) {
    state.loading = loading;
  },

  // Set error
  SET_ERROR(state, error) {
    state.error = error;
  },

  // Clear error
  CLEAR_ERROR(state) {
    state.error = null;
  },

  // Set printers
  SET_PRINTERS(state, printers) {
    state.printers = printers;
    state.lastUpdated = new Date();
    state.statistics = calculateStatistics(state);
  },

  // Add printer
  ADD_PRINTER(state, printer) {
    state.printers.push(printer);
    state.lastUpdated = new Date();
    state.statistics = calculateStatistics(state);
  },

  // Update printer
  UPDATE_PRINTER(state, updatedPrinter) {
    const index = state.printers.findIndex(
      (printer) => printer.id === updatedPrinter.id
    );
    if (index !== -1) {
      state.printers.splice(index, 1, updatedPrinter);
      state.lastUpdated = new Date();
      state.statistics = calculateStatistics(state);
    }
  },

  // Remove printer
  REMOVE_PRINTER(state, printerId) {
    state.printers = state.printers.filter(
      (printer) => printer.id !== printerId
    );
    state.lastUpdated = new Date();
    state.statistics = calculateStatistics(state);
  },

  // Update printer connection status
  UPDATE_PRINTER_STATUS(state, { printerId, status }) {
    const printer = state.printers.find((p) => p.id === printerId);
    if (printer) {
      printer.connection_status = status;
      printer.last_status_check = new Date();
      state.statistics = calculateStatistics(state);
    }
  },

  // Set print jobs
  SET_PRINT_JOBS(state, printJobs) {
    state.printJobs = printJobs;
    state.statistics = calculateStatistics(state);
  },

  // Add print job
  ADD_PRINT_JOB(state, printJob) {
    state.printJobs.unshift(printJob);
    state.statistics = calculateStatistics(state);
  },

  // Update print job
  UPDATE_PRINT_JOB(state, updatedJob) {
    const index = state.printJobs.findIndex((job) => job.id === updatedJob.id);
    if (index !== -1) {
      state.printJobs.splice(index, 1, updatedJob);
      state.statistics = calculateStatistics(state);
    }
  },

  // Remove print job
  REMOVE_PRINT_JOB(state, jobId) {
    state.printJobs = state.printJobs.filter((job) => job.id !== jobId);
    state.statistics = calculateStatistics(state);
  },

  // Add job to print queue
  ADD_TO_QUEUE(state, job) {
    state.printQueue.push(job);
  },

  // Remove job from print queue
  REMOVE_FROM_QUEUE(state, jobId) {
    state.printQueue = state.printQueue.filter((job) => job.id !== jobId);
  },

  // Update job status in queue
  UPDATE_QUEUE_JOB_STATUS(state, { jobId, status }) {
    const job = state.printQueue.find((job) => job.id === jobId);
    if (job) {
      job.status = status;
      if (status === "completed" || status === "failed") {
        job.completedAt = new Date();
      }
    }
  },

  // Clear print queue
  CLEAR_QUEUE(state) {
    state.printQueue = [];
  },

  // Set statistics
  SET_STATISTICS(state, statistics) {
    state.statistics = { ...state.statistics, ...statistics };
  },
};

const actions = {
  // Initialize printer service
  initializePrinterService({ commit, rootGetters }) {
    const axios = rootGetters["axios"];
    if (axios) {
      // Store printer service instance for later use
      commit("SET_PRINTER_SERVICE", new PrinterService(axios));
    }
  },

  // Fetch all printers
  async fetchPrinters({ commit }, axiosInstance) {
    commit("SET_LOADING", true);
    commit("CLEAR_ERROR");

    try {
      const axios = axiosInstance || this.$axios;
      const response = await axios.get("/printers");
      commit("SET_PRINTERS", response.data.data || []);
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch printers";
      commit("SET_ERROR", errorMessage);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  // Create new printer
  async createPrinter({ commit }, { printerData, axiosInstance }) {
    commit("SET_LOADING", true);
    commit("CLEAR_ERROR");

    try {
      const axios = axiosInstance || this.$axios;
      const response = await axios.post("/printers", printerData);
      commit("ADD_PRINTER", response.data.data);
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to create printer";
      commit("SET_ERROR", errorMessage);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  // Update existing printer
  async updatePrinter({ commit }, { id, data, axiosInstance }) {
    commit("SET_LOADING", true);
    commit("CLEAR_ERROR");

    try {
      const axios = axiosInstance || this.$axios;
      const response = await axios.put(`/printers/${id}`, data);
      commit("UPDATE_PRINTER", response.data.data);
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to update printer";
      commit("SET_ERROR", errorMessage);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  // Delete printer
  async deletePrinter({ commit }, { printerId, axiosInstance }) {
    commit("SET_LOADING", true);
    commit("CLEAR_ERROR");

    try {
      const axios = axiosInstance || this.$axios;
      await axios.delete(`/printers/${printerId}`);
      commit("REMOVE_PRINTER", printerId);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to delete printer";
      commit("SET_ERROR", errorMessage);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  // Test printer connection
  async testConnection({ commit }, { printerId, axiosInstance }) {
    commit("CLEAR_ERROR");

    try {
      const axios = axiosInstance || this.$axios;
      const response = await axios.post(
        `/printers/${printerId}/test-connection`
      );

      // Update printer status based on test result
      const status = response.data.success ? "online" : "offline";
      commit("UPDATE_PRINTER_STATUS", { printerId, status });

      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Connection test failed";
      commit("SET_ERROR", errorMessage);
      commit("UPDATE_PRINTER_STATUS", { printerId, status: "offline" });
      throw error;
    }
  },

  // Discover network printers
  async discoverNetworkPrinters({ commit }) {
    commit("SET_LOADING", true);
    commit("CLEAR_ERROR");

    try {
      const response = await this.$axios.post("/api/printers/discover-network");
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Network discovery failed";
      commit("SET_ERROR", errorMessage);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  // Send print job
  async sendPrintJob({ commit, dispatch }, { printerId, printJob }) {
    commit("CLEAR_ERROR");

    try {
      const response = await this.$axios.post("/api/printers/print", {
        printer_id: printerId,
        print_job: printJob,
      });

      // Add to local print jobs tracking
      commit("ADD_PRINT_JOB", {
        id: response.data.jobId || Date.now(),
        printer_id: printerId,
        ...printJob,
        status: "sent",
        created_at: new Date().toISOString(),
      });

      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to send print job";
      commit("SET_ERROR", errorMessage);
      throw error;
    }
  },

  // Fetch print jobs
  async fetchPrintJobs({ commit }, filters = {}) {
    commit("SET_LOADING", true);
    commit("CLEAR_ERROR");

    try {
      const params = new URLSearchParams(filters);
      const response = await this.$axios.get(`/api/print-jobs?${params}`);
      commit("SET_PRINT_JOBS", response.data.data || []);
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch print jobs";
      commit("SET_ERROR", errorMessage);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  // Retry failed print job
  async retryPrintJob({ commit }, jobId) {
    commit("CLEAR_ERROR");

    try {
      const response = await this.$axios.post(`/api/print-jobs/${jobId}/retry`);

      // Update job status
      commit("UPDATE_PRINT_JOB", {
        id: jobId,
        status: "retrying",
        retry_count: (response.data.retryCount || 0) + 1,
      });

      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to retry print job";
      commit("SET_ERROR", errorMessage);
      throw error;
    }
  },

  // Cancel print job
  async cancelPrintJob({ commit }, jobId) {
    commit("CLEAR_ERROR");

    try {
      await this.$axios.delete(`/api/print-jobs/${jobId}`);
      commit("REMOVE_PRINT_JOB", jobId);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to cancel print job";
      commit("SET_ERROR", errorMessage);
      throw error;
    }
  },

  // Print kitchen order (convenience method)
  async printKitchenOrder({ commit, getters, dispatch }, orderData) {
    const printer = getters.defaultKitchenPrinter;

    if (!printer) {
      throw new Error("No default kitchen printer available");
    }

    const printJob = {
      type: "kitchen_order",
      content: generateKitchenOrderContent(orderData),
      options: {
        copies: 1,
        cut_paper: true,
      },
      order_data: orderData,
    };

    return await dispatch("sendPrintJob", {
      printerId: printer.id,
      printJob,
    });
  },

  // Print receipt (convenience method)
  async printReceipt({ commit, getters, dispatch }, receiptData) {
    const printers = getters.availablePrinters("receipt");

    if (printers.length === 0) {
      throw new Error("No receipt printer available");
    }

    const printer = printers[0]; // Use first available receipt printer

    const printJob = {
      type: "receipt",
      content: generateReceiptContent(receiptData),
      options: {
        copies: 1,
        cut_paper: true,
      },
      receipt_data: receiptData,
    };

    return await dispatch("sendPrintJob", {
      printerId: printer.id,
      printJob,
    });
  },

  // Check printer status periodically
  async checkPrintersStatus({ commit, dispatch }) {
    try {
      const printers = await dispatch("fetchPrinters");
      const statusPromises = printers.data.map((printer) =>
        dispatch("testConnection", printer.id).catch((error) => {
          console.warn(`Status check failed for printer ${printer.id}:`, error);
        })
      );

      await Promise.allSettled(statusPromises);
    } catch (error) {
      console.error("Error checking printer status:", error);
    }
  },

  // Clear all errors
  clearError({ commit }) {
    commit("CLEAR_ERROR");
  },
};

// Helper function to calculate statistics
function calculateStatistics(state) {
  const onlinePrinters = state.printers.filter(
    (p) => p.connection_status === "online"
  ).length;
  const offlinePrinters = state.printers.filter(
    (p) => p.connection_status === "offline"
  ).length;

  const today = new Date().toDateString();
  const todayPrintJobs = state.printJobs.filter(
    (job) => new Date(job.created_at).toDateString() === today
  ).length;

  const pendingPrintJobs = state.printJobs.filter(
    (job) => job.status === "pending"
  ).length;
  const failedPrintJobs = state.printJobs.filter(
    (job) => job.status === "failed"
  ).length;

  return {
    onlinePrinters,
    offlinePrinters,
    todayPrintJobs,
    pendingPrintJobs,
    failedPrintJobs,
    totalPrinters: state.printers.length,
    totalPrintJobs: state.printJobs.length,
  };
}

// Helper function to generate kitchen order content
function generateKitchenOrderContent(orderData) {
  let content = `=== KITCHEN ORDER ===\n`;
  content += `Table: ${orderData.tableName || "N/A"}\n`;
  content += `Order: ${orderData.orderNumber || "N/A"}\n`;
  content += `Time: ${new Date().toLocaleString()}\n`;
  content += `Staff: ${orderData.staffName || "N/A"}\n`;
  content += `--------------------------------\n`;

  if (orderData.items && orderData.items.length > 0) {
    orderData.items.forEach((item) => {
      content += `${item.quantity}x ${item.name}\n`;
      if (item.notes || item.modifications) {
        content += `  Note: ${item.notes || item.modifications}\n`;
      }
    });
  }

  content += `================================\n`;
  content += `END OF ORDER\n`;

  return content;
}

// Helper function to generate receipt content
function generateReceiptContent(receiptData) {
  let content = `=== RECEIPT ===\n`;
  content += `Table: ${receiptData.tableName || "N/A"}\n`;
  content += `Receipt: ${receiptData.receiptNumber || "N/A"}\n`;
  content += `Date: ${new Date().toLocaleString()}\n`;
  content += `Staff: ${receiptData.staffName || "N/A"}\n`;
  content += `--------------------------------\n`;

  if (receiptData.items && receiptData.items.length > 0) {
    receiptData.items.forEach((item) => {
      content += `${item.name} ${item.quantity}x${item.price}\n`;
    });
  }

  content += `--------------------------------\n`;
  content += `TOTAL: ${receiptData.total || "0.00"}\n`;

  if (receiptData.paymentMethod) {
    content += `Payment: ${receiptData.paymentMethod}\n`;
  }

  if (receiptData.change) {
    content += `Change: ${receiptData.change}\n`;
  }

  content += `================================\n`;
  content += `Thank you for dining!\n`;

  return content;
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
